import {createSlice} from '@reduxjs/toolkit';
import {getZoneChangeRequests} from './zoneChange';

export const defaultBlockColumnVisibility = {
  area: true,
  rowSpacing: true,
  rowCount: true,
  rowBearing: false,
  cropType: true,
  cropVariety: true,
  rowPassEnabled: false,
  parentField: true,
  parentRegion: false,
  reviewStatus: false,
};

export const BLOCK_TABLE_COLUMNS_MENU_CONFIG = [
  {key: 'area', label: 'Area'},
  {key: 'rowSpacing', label: 'Row Spacing'},
  {key: 'rowCount', label: 'Row Count'},
  {key: 'rowBearing', label: 'Row Bearing'},
  {key: 'cropType', label: 'Crop Type'},
  {key: 'cropVariety', label: 'Crop Variety'},
  {key: 'rowPassEnabled', label: 'Row Pass Enabled', adminOnly: true},
  {key: 'parentField', label: 'Parent Field'},
  {key: 'parentRegion', label: 'Parent Region'},
  {key: 'reviewStatus', label: 'Review Status'},
];

const zoneInfoSlice = createSlice({
  name: 'zoneinfo',
  initialState: {
    loading: true,
    zonesDicts: {
      blocks: {},
      fields: {},
      regions: {},
      nonAcAreas: {},
    },
    draftBlocks: {}, // drafted "Blocks" from drawing, about to be saved
    draftNonAcAreas: {}, // drafted “Other Areas” from drawing, about to be saved
    geofencesDict: {},
    nonAcGeofencesDict: {},
    displayedTable: 'block',
    cropTypesDict: {}, // Store crop data as {primaryClass: [subClass...]}
    cropTypesDropdownOptions: [], // Store formated crop type edit dropdown options
    editZoneInfo: false,
    editSnapshotVersion: 0, // Counter used to update the snapshot being used for editable layer
    editZoneInfoValues: {},
    sortMethod: 'name',
    statusFilter: 'active',
    mapView: 1,
    zoneZoom: '',
    madCoreZoomId: '',
    madLoading: true,
    vehicleSNDict: {},
    todayOnly: false,
    rowPassEnabledData: {},
    missingActivityData: {},
    missingActivityState: {
      displayedGeometries: 'all',
      displayGPS: true,
    },
    // Per-row selection for the Missing Activity table's Dismiss/Import action.
    // Shape: { [detectionId]: 'dismiss' | 'import' }. A given id can only hold
    // one value at a time, which enforces the mutual exclusivity between the
    // Dismiss and Import checkboxes for that row.
    madArchiveSelection: {},
    // When true, the MAD table allows inline edits (group names only for now).
    madEditMode: false,
    // Draft MAD edit values while madEditMode is active. Additional edit fields
    // can be added alongside groupNames as the table grows.
    // Shape: { groupNames: { [groupId]: string } }
    madEditValues: {groupNames: {}},
    showCreateZoneForm: false,
    createZoneFormType: null,
    toolMode: 'view', // 'view' | 'select' | 'draw'
    blockColumnVisibility: {...defaultBlockColumnVisibility},
    lastSelectedFeatureIndex: null, // to persist the selected features across rerenders (any geometry edits)
    totalPointsAdded: 0, // to track the total number of points added
    // Menu dispatches this so ZoneInfoTable can open the standalone new field/region dialog (nonce bumps each click).
    standaloneFieldRegionModalRequest: {nonce: 0, mode: null},
    // Bounding box of the last created or edited zone — Map.js zooms here after save
    // instead of running showAll(). Cleared once consumed by Map.js.
    // Shape: { latMin, latMax, lngMin, lngMax } | null
    lastSavedZoneBounds: null,
    // GET /mapview/zonePermissions: what the user may do and from which date (null until loaded)
    zonePermissions: null,
    // GET /mapview/zones/requests: the customer's pending support requests
    zoneChangeRequests: [],
    // A save asked for row passes without a row direction: IntelliCulture turns them on (shows a note)
    rowPassRequested: false,
  },
  reducers: {
    updateLoading(state, action) {
      state.loading = action.payload;
    },
    updateMadLoading(state, action) {
      state.madLoading = action.payload;
    },
    updateZoneInfoData(state, action) {
      const allZoneData = action.payload;

      state.zonesDicts = {
        blocks: allZoneData.blocks,
        fields: allZoneData.fields,
        regions: allZoneData.regions,
        nonAcAreas: allZoneData.nonAcAreas,
      };
      state.geofencesDict = allZoneData.geofences;
      state.nonAcGeofencesDict = allZoneData.nonAcGeofences;
    },
    updateMissingActivityData(state, action) {
      state.vehicleSNDict = action.payload.vehicleSNDict;
      state.missingActivityData = action.payload;
      // Fresh data means any prior row selection no longer maps cleanly to the
      // current rows (archived rows are now gone), so reset the selection.
      state.madArchiveSelection = {};
      // Also abandon any in-progress MAD edits.
      state.madEditMode = false;
      state.madEditValues = {groupNames: {}};
    },
    updateMissingActivityState(state, action) {
      state.missingActivityState = action.payload;
    },
    updateDisplayedTable(state, action) {
      state.displayedTable = action.payload;
      // Switching tables abandons any in-progress MAD selection.
      state.madArchiveSelection = {};
      state.madEditMode = false;
      state.madEditValues = {groupNames: {}};
    },
    // Replace the entire Dismiss/Import selection map for the MAD table. The
    // table builds the next map (applying mutual exclusivity) and dispatches it.
    setMadArchiveSelection(state, action) {
      state.madArchiveSelection = action.payload;
    },
    // Clear the MAD selection, e.g. after a successful archive action.
    clearMadArchiveSelection(state) {
      state.madArchiveSelection = {};
    },
    // Drop archived detections from in-memory MAD data so the table/map update
    // immediately without a full re-fetch from the MAD service.
    removeArchivedMissingActivity(state, action) {
      const archivedIds = action.payload?.archivedIds;
      if (!Array.isArray(archivedIds) || archivedIds.length === 0) {
        return;
      }

      const mad = state.missingActivityData;
      if (!mad || typeof mad !== 'object') {
        state.madArchiveSelection = {};
        return;
      }

      const archivedIdSet = new Set(
        archivedIds.map((id) => {
          return String(id);
        })
      );

      if (Array.isArray(mad.tableData)) {
        mad.tableData = mad.tableData.filter((row) => {
          return !archivedIdSet.has(String(row.id));
        });
      }

      archivedIds.forEach((id) => {
        const idStr = String(id);
        if (mad.detectionsById && typeof mad.detectionsById === 'object') {
          delete mad.detectionsById[idStr];
          delete mad.detectionsById[id];
        }
        if (mad.gpsDataById && typeof mad.gpsDataById === 'object') {
          delete mad.gpsDataById[idStr];
          delete mad.gpsDataById[id];
        }
      });

      state.madArchiveSelection = {};
    },
    // Toggle inline editing in the MAD table (group names only for now).
    setMadEditMode(state, action) {
      state.madEditMode = action.payload;
      // Leaving edit mode clears drafts so stale values don't linger.
      if (!action.payload) {
        state.madEditValues = {groupNames: {}};
      }
    },
    // Replace the entire draft map for MAD inline edits.
    setMadEditValues(state, action) {
      state.madEditValues = action.payload;
    },
    // Optimistically patch group names in tableData and detectionsById so the
    // table, map labels, and name filter options update without a full re-fetch.
    updateMissingActivityGroupNames(state, action) {
      const updates = action.payload?.updates;
      if (!Array.isArray(updates) || updates.length === 0) {
        return;
      }

      const mad = state.missingActivityData;
      if (!mad || typeof mad !== 'object') {
        return;
      }

      updates.forEach((update) => {
        const groupId = update?.group;
        const areaName = typeof update?.areaName === 'string' ? update.areaName.trim() : '';
        if (groupId == null || areaName === '') {
          return;
        }

        const groupKey = String(groupId);

        if (Array.isArray(mad.tableData)) {
          mad.tableData.forEach((row) => {
            if (row && String(row.group) === groupKey) {
              row.areaName = areaName;
            }
          });
        }

        if (mad.detectionsById && typeof mad.detectionsById === 'object') {
          Object.keys(mad.detectionsById).forEach((id) => {
            const detection = mad.detectionsById[id];
            if (detection && String(detection.group) === groupKey) {
              detection.areaname = areaName;
            }
          });
        }
      });
    },
    updateCropTypesDict(state, action) {
      state.cropTypesDict = action.payload;
    },
    updateCropTypesDropdownOptions(state, action) {
      state.cropTypesDropdownOptions = action.payload;
    },
    updateEditZoneInfo(state, action) {
      const editZoneInfoBool = action.payload;

      state.editZoneInfo = editZoneInfoBool;
      if (!editZoneInfoBool) {
        state.editZoneInfoValues = {};
        state.standaloneFieldRegionModalRequest = {nonce: 0, mode: null};
      }
    },
    updateEditZoneInfoValues(state, action) {
      state.editZoneInfoValues = action.payload;
    },
    resetEditSnapshot(state) {
      state.editSnapshotVersion = (state.editSnapshotVersion || 0) + 1;
    },
    updateDisplayedColumn(state, action) {
      state.displayedColumn = action.payload;
    },
    updateSortMethod(state, action) {
      state.sortMethod = action.payload;
    },
    updateStatusFilter(state, action) {
      state.statusFilter = action.payload;
    },
    updateMapView(state, action) {
      // 0: Only table, 1: Table and map, 2: Only map
      const mapSetting = action.payload;
      if (mapSetting == undefined) {
        state.mapView = (state.mapView + 1) % 3;
      } else {
        state.mapView = mapSetting;
      }
    },
    updateZoneZoom(state, action) {
      state.zoneZoom = action.payload;
    },
    updateMADCoreZoomId(state, action) {
      state.madCoreZoomId = action.payload;
    },
    updateRowPassEnabledData(state, action) {
      state.rowPassEnabledData = action.payload;
    },
    createDraftBlock(state, action) {
      const {id, block} = action.payload;
      state.draftBlocks[id] = block;
    },
    discardDraftBlock(state, action) {
      const id = action.payload;
      if (state.draftBlocks && state.draftBlocks[id]) {
        delete state.draftBlocks[id];
      }
    },
    createDraftNonAcArea(state, action) {
      const {id, nonAcArea} = action.payload;
      state.draftNonAcAreas[id] = nonAcArea;
    },
    discardDraftNonAcArea(state, action) {
      const id = action.payload;
      if (state.draftNonAcAreas && state.draftNonAcAreas[id]) {
        delete state.draftNonAcAreas[id];
      }
    },
    openCreateZoneForm(state, action) {
      state.createZoneFormType = action.payload;
      state.showCreateZoneForm = true;
    },
    closeCreateZoneForm(state) {
      state.showCreateZoneForm = false;
      state.createZoneFormType = null;
    },
    updateToolMode(state, action) {
      state.toolMode = action.payload;
    },
    updateBlockColumnVisibility(state, action) {
      Object.assign(state.blockColumnVisibility, action.payload);
    },
    updateLastSelectedFeatureIndex(state, action) {
      state.lastSelectedFeatureIndex = action.payload;
    },
    updateTotalPointsAdded(state, action) {
      state.totalPointsAdded = action.payload;
    },
    incrementTotalPointsAdded(state) {
      state.totalPointsAdded = state.totalPointsAdded + 1;
    },
    requestStandaloneFieldRegionModal(state, action) {
      const mode = action.payload;
      state.standaloneFieldRegionModalRequest = {
        nonce: state.standaloneFieldRegionModalRequest.nonce + 1,
        mode,
      };
    },
    clearStandaloneFieldRegionModalRequest(state) {
      state.standaloneFieldRegionModalRequest = {nonce: 0, mode: null};
    },
    // Set the bounding box of the most recently created or edited zone so Map.js
    // can zoom to it after the save completes. Pass null to clear.
    updateLastSavedZoneBounds(state, action) {
      state.lastSavedZoneBounds = action.payload;
    },
    updateZonePermissions(state, action) {
      state.zonePermissions = action.payload;
    },
    updateRowPassRequested(state, action) {
      state.rowPassRequested = action.payload;
    },
    updateZoneChangeRequests(state, action) {
      state.zoneChangeRequests = action.payload;
    },
  },
});

export const {
  updateLoading,
  updateDisplayedTable,
  updateSortMethod,
  updateStatusFilter,
  updateMapView,
  updateZoneZoom,
  updateMADCoreZoomId,
  updateZoneInfoData,
  updateMissingActivityData,
  updateMissingActivityState,
  setMadArchiveSelection,
  clearMadArchiveSelection,
  removeArchivedMissingActivity,
  setMadEditMode,
  setMadEditValues,
  updateMissingActivityGroupNames,
  updateCropTypesDict,
  updateCropTypesDropdownOptions,
  updateEditZoneInfo,
  updateEditZoneInfoValues,
  updateRowPassEnabledData,
  updateMadLoading,
  createDraftBlock,
  discardDraftBlock,
  createDraftNonAcArea,
  discardDraftNonAcArea,
  resetEditSnapshot,
  openCreateZoneForm,
  closeCreateZoneForm,
  updateToolMode,
  updateBlockColumnVisibility,
  updateLastSelectedFeatureIndex,
  updateTotalPointsAdded,
  incrementTotalPointsAdded,
  requestStandaloneFieldRegionModal,
  clearStandaloneFieldRegionModalRequest,
  updateLastSavedZoneBounds,
  updateZonePermissions,
  updateZoneChangeRequests,
  updateRowPassRequested,
} = zoneInfoSlice.actions;
export default zoneInfoSlice.reducer;

/**
 * Reloads the customer's pending support requests (the "Waiting for IntelliCulture" notes). A failed load keeps
 * the last list.
 *
 * @return {Function} thunk
 */
export function refreshZoneChangeRequests() {
  return async (dispatch) => {
    const result = await getZoneChangeRequests();
    if (result.ok && Array.isArray(result.body)) {
      dispatch(updateZoneChangeRequests(result.body));
    }
  };
}
